<template>
  <b-modal
    :visible="visible"
    scrollable
    centered
    :hide-footer="contactList && contactList.length === 0"
    @hide="onHide"
    @ok="onCallInitiated"
    :ok-disabled="!selectedContactWithNumber"
    title-class="text-center w-100"
    footer-class="flex-row-reverse justify-content-center"
    cancel-variant="outline-primary"
  >
    <template #modal-title>Who do you want to call?</template>
    <template #modal-ok>Call now</template>
    <b-form-group
      v-if="contactList && contactList.length > 0"
      v-slot="{ ariaDescribedby }"
    >
      <div v-for="c in contactList" :key="c.id" class="py-1">
        <div
          :class="{
            'flex-column': isNameTooLong(c),
            'd-flex align-items-center': !isNameTooLong(c),
          }"
        >
          <b-form-radio
            v-model="selectedContact"
            :aria-describedby="ariaDescribedby"
            name="select-contact"
            :value="c"
            :class="{ 'mb-2': isNameTooLong(c) }"
            @change="handleContactSelection(c)"
          >
            {{ c.lastName }} {{ c.firstName }}
            <p
              role="button"
              title="Click to copy"
              @click="copyToClipboard(c.email, c.id)"
              v-if="c.email"
              class="mb-2 contact-email"
              :ref="`email-${c.id}`"
            >
              {{ c.email }}
            </p>
          </b-form-radio>
          <b-form-select
            v-model="selectedPhoneType[c.id]"
            :options="getPhoneOptions(c)"
            :class="{ 'ml-4': !isNameTooLong(c) }"
            size="sm"
            :disabled="!isContactSelected(c)"
          ></b-form-select>
        </div>
      </div>
    </b-form-group>
    <span v-else class="text-center d-inline-block w-100 py-3">
      There are no contacts available for this account.
    </span>
  </b-modal>
</template>

<script>
export default {
  props: {
    contactList: {
      type: Array,
      default: () => [],
    },
    visible: Boolean,
  },
  data() {
    return {
      selectedContact: null,
      selectedPhoneType: {},
      NAME_LENGTH_THRESHOLD: 30, // Maximum combined length before switching to vertical layout
      clicks: 0,
      timer: {},
    };
  },
  computed: {
    selectedContactWithNumber() {
      if (!this.selectedContact) return null;
      const phoneType = this.selectedPhoneType[this.selectedContact.id];
      return phoneType
        ? { ...this.selectedContact, selectedPhoneType: phoneType }
        : null;
    },
  },
  watch: {
    contactList: {
      handler(value) {
        if (value.length === 1) {
          this.selectedContact = value[0];
          this.handleContactSelection(value[0]);
        }
        // Initialize phone types for all contacts
        value.forEach((contact) => {
          if (!this.selectedPhoneType[contact.id]) {
            this.selectedPhoneType[contact.id] =
              this.getDefaultPhoneType(contact);
          }
        });
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async copyToClipboard(email, id) {
      try {
        // Try modern clipboard API first
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(email);
        } else {
          // Fallback using the email element's text content
          const emailElement = this.$refs[`email-${id}`][0];
          const range = document.createRange();
          const selection = window.getSelection();

          range.selectNodeContents(emailElement);
          selection.removeAllRanges();
          selection.addRange(range);
          document.execCommand('copy');

          // Clear selection
          selection.removeAllRanges();
        }

        this.$bvToast.toast(`${email} copied`, {
          title: 'Success',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        });
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    },
    isNameTooLong(contact) {
      const fullName = `${contact.lastName} ${contact.firstName}`;
      return fullName.length > this.NAME_LENGTH_THRESHOLD;
    },
    isContactSelected(contact) {
      return this.selectedContact && this.selectedContact.id === contact.id;
    },
    getPhoneOptions(contact) {
      const options = [];
      if (contact.phone) {
        options.push({ value: 'phone', text: `Phone: ${contact.phone}` });
      }
      if (contact.mobilePhone) {
        options.push({
          value: 'mobilePhone',
          text: `Mobile: ${contact.mobilePhone}`,
        });
      }
      return options;
    },
    getDefaultPhoneType(contact) {
      if (contact.mobilePhone) return 'mobilePhone';
      if (contact.phone) return 'phone';
      return null;
    },
    handleContactSelection(contact) {
      if (!this.selectedPhoneType[contact.id]) {
        this.selectedPhoneType[contact.id] = this.getDefaultPhoneType(contact);
      }
    },
    onHide(bvModalEvent) {
      this.$emit('hide');
    },
    onCallInitiated() {
      const phoneType = this.selectedPhoneType[this.selectedContact.id];
      const phoneNumber = this.selectedContact[phoneType];
      this.$emit('call-initiated', {
        ...this.selectedContact,
        phone: phoneNumber,
      });
    },
  },
  created() {
    // Initialize phone types for all contacts on component creation
    if (this.contactList) {
      this.contactList.forEach((contact) => {
        this.selectedPhoneType[contact.id] = this.getDefaultPhoneType(contact);
      });
    }
  },
};
</script>

<style scoped>
.w-auto {
  width: auto !important;
}
.contact-email {
  cursor: pointer;
}
.contact-email:hover {
  color: #04a789;
}
</style>
